import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

/**
 * @typedef {Object} FocusManagementOptions
 * @property {import('vue').Ref<HTMLElement | null>} containerRef - Ref pointing to the container element whose focusable children should be managed.
 * @property {boolean} [trapFocus=false] - Whether to trap focus within the container.
 * @property {import('vue').Ref<boolean>} [focusFirstOnActivate=ref(false)] - Ref indicating whether to focus the first element when the focus management becomes active.
 * @property {string} [initialFocusSelector=null] - Selector for the element to focus initially if focusFirstOnActivate.value is false.
 * @property {import('vue').Ref<boolean>} [isActive=ref(true)] - Ref to control whether the focus management is active.
 * @property {import('vue').Ref<HTMLElement | null>} [focusTriggerElementRef=ref(null)] - Ref pointing to the element that triggered the focus management (for focus restoration).
 * @property {(event: KeyboardEvent) => void} [onElementActivated=null] - Optional callback when an element inside the container is activated (e.g., via Enter or Space).
 * @property {import('vue').Ref<boolean>} [shouldRestoreFocus=ref(true)] - Ref indicating whether focus should be restored when deactivating.
 */

/**
 * Composable for managing focus within a component or container.
 * Handles focus trapping, sequential navigation (Tab/Shift+Tab), initial focus, and focus restoration.
 *
 * @param {FocusManagementOptions} options
 */
export function useFocusManagement(options) {
  const {
    containerRef,
    focusFirstOnActivate = ref(false),
    focusTriggerElementRef = ref(null),
    initialFocusSelector = null,
    isActive = ref(true),
    onElementActivated = null,
    shouldRestoreFocus = ref(true),
    trapFocus = false,
  } = options;

  const focusableElements = ref([]);
  const currentFocusIndex = ref(-1);
  const isInitialized = ref(false);

  /** Gets all focusable elements within the container. */
  const getFocusableElements = () => {
    if (!containerRef.value) return [];
    const elements = Array.from(
      containerRef.value.querySelectorAll(
        'a[href]:not([disabled]), button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => {
      // Check if the element is visible and has a size
      const rect = el.getBoundingClientRect();
      return (
        el.offsetParent !== null &&
        window.getComputedStyle(el).visibility !== 'hidden' &&
        window.getComputedStyle(el).display !== 'none' &&
        rect.width > 0 &&
        rect.height > 0
      );
    });
    return elements;
  };

  /** Updates the list of focusable elements. */
  const updateFocusableElements = () => {
    focusableElements.value = getFocusableElements();
  };

  /** Focuses an element in the list by its index. */
  const focusElementByIndex = (index) => {
    if (index >= 0 && index < focusableElements.value.length) {
      const elementToFocus = focusableElements.value[index];
      elementToFocus.focus();
      currentFocusIndex.value = index;
    }
  };

  /** Focuses the first focusable element. */
  const focusFirstElement = () => {
    updateFocusableElements();
    focusElementByIndex(0);
  };

  /** Focuses the last focusable element. */
  const focusLastElement = () => {
    updateFocusableElements();
    focusElementByIndex(focusableElements.value.length - 1);
  };

  /** Focuses the designated initial element based on options. */
  const focusInitialElement = () => {
    updateFocusableElements();
    if (!focusableElements.value.length || !focusFirstOnActivate.value) return;

    if (initialFocusSelector) {
      const targetElement = containerRef.value?.querySelector(initialFocusSelector);
      const index = focusableElements.value.findIndex((el) => el === targetElement);
      if (index !== -1) {
        focusElementByIndex(index);
        return;
      }
    }

    focusFirstElement();
  };

  /** Restores focus to the element that triggered the focus management. */
  const restoreFocus = () => {
    if (focusTriggerElementRef.value) {
      focusTriggerElementRef.value.focus();
    }
  };

  /** Handles Tab, Enter, Space key presses for focus navigation/trapping and activation. */
  const handleKeyDown = (event) => {
    const { key } = event;
    if (!isActive.value || !containerRef.value) return;

    if (key === 'Enter' || key === ' ') {
      if (onElementActivated) {
        onElementActivated(event);
      }
    }

    if (key === 'Tab') {
      updateFocusableElements();
      if (focusableElements.value.length === 0) return;

      const focusedElement = document.activeElement;
      const isFocusInside = containerRef.value.contains(focusedElement);
      currentFocusIndex.value = focusableElements.value.findIndex((el) => el === focusedElement);

      if (event.shiftKey) {
        if (!isFocusInside || currentFocusIndex.value === 0) {
          if (trapFocus) {
            event.preventDefault();
            focusLastElement();
          }
        }
      } else {
        if (!isFocusInside) {
          if (trapFocus) {
            event.preventDefault();
            focusFirstElement();
          }
        } else if (currentFocusIndex.value === focusableElements.value.length - 1) {
          if (trapFocus) {
            event.preventDefault();
            focusFirstElement();
          }
        }
      }
    }
  };

  /** Adds the keydown listener. */
  const addListener = () => {
    const container = containerRef.value;
    if (!container) return;
    container.addEventListener('keydown', handleKeyDown, true);
  };

  /** Removes the keydown listener. */
  const removeListener = () => {
    const container = containerRef.value;
    if (!container) return;
    container.removeEventListener('keydown', handleKeyDown, true);
  };

  /**
   * Acts as initiator and deactivator of focus management,
   * and also controls focus restoration when deactivating,
   * if active set up focus trapping else remove it
   */
  watch(
    isActive,
    (newIsActive, oldIsActive) => {
      if (newIsActive) {
        setTimeout(() => {
          if (containerRef.value) {
            focusInitialElement();
            addListener();
          }
        }, 100);
      } else {
        removeListener();
        if (oldIsActive && shouldRestoreFocus.value) {
          restoreFocus();
        }
      }
    },
    { immediate: false },
  );

  /**
   * Handles dynamic changes to the container element itself.
   * If the containerRef points to a new element while focus management is active,
   * it cleans up the event listener from the old container, attaches it to the new one,
   * and updates the list of focusable elements. Handles removal of the container too.
   */
  watch(containerRef, (newContainer, oldContainer) => {
    if (isActive.value) {
      if (oldContainer) {
        oldContainer.removeEventListener('keydown', handleKeyDown, true);
      }
      if (newContainer) {
        newContainer.addEventListener('keydown', handleKeyDown, true);
        updateFocusableElements();
      } else {
        focusableElements.value = [];
      }
    }
  });

  onMounted(() => {
    if (isActive.value && containerRef.value) {
      focusInitialElement();
      addListener();
    }
    isInitialized.value = true;
  });

  onBeforeUnmount(() => {
    removeListener();
  });

  return {
    currentFocusIndex,
    focusableElements,
    focusElementByIndex,
    focusFirstElement,
    focusLastElement,
    restoreFocus,
    updateFocusableElements,
  };
}
